/**
 * Speech ASR - Real-time Speech Recognition SDK  
 * Browser-based ASR using sherpa-onnx WebAssembly
 */

export class SpeechASR {
  constructor(options = {}) {
    const rawTwoPassMode = options.twoPass?.mode;
    const twoPassMode = (rawTwoPassMode === 'manual-stop' || rawTwoPassMode === 'manual')
      ? 'manual-stop'
      : 'auto';

    this.options = {
      vadMode: options.vadMode || 'off',
      onlineModel: options.onlineModel || 'zipformer',
      modelPaths: options.modelPaths || {},
      twoPass: {
        // Default off so missing offline assets/weight packs don't break init
        enabled: options.twoPass?.enabled === true,
        backend: options.twoPass?.backend || 'wasm',
        // auto: run 2nd pass for each segment; manual-stop: only once after stop()
        mode: twoPassMode,
        // When true and mode=manual-stop, skip online first-pass to save CPU
        disableOnlineInManual: options.twoPass?.disableOnlineInManual === true,
        // Pre-warm worker/models after init to reduce first 2-pass latency (only when enabled)
        autoWarmup: options.twoPass?.autoWarmup !== false,
      },
      punctuation: {
        enabled: options.punctuation?.enabled || false,
        modelPath: options.punctuation?.modelPath || '',
      },
      vad: {
        silero: {
          // Align with working app-asr defaults
          threshold: options.vad?.silero?.threshold || 0.5,
          minSilenceDuration: options.vad?.silero?.minSilenceDuration || 0.3,
          minSpeechDuration: options.vad?.silero?.minSpeechDuration || 0.25,
          maxSpeechDuration: options.vad?.silero?.maxSpeechDuration || 20,
          windowSize: options.vad?.silero?.windowSize || 512,
        },
        bufferSizeInSeconds: options.vad?.bufferSizeInSeconds || 60,
      },
      sampleRate: options.sampleRate || 16000,
      bufferSize: options.bufferSize || 4096,
      onResult: options.onResult || null,
      onPunctuated: options.onPunctuated || options.onResult || null,
      onPartial: options.onPartial || null,
      onTwoPassResult: options.onTwoPassResult || null,
      onTwoPassError: options.onTwoPassError || null,
      onTwoPassStart: options.onTwoPassStart || null,
      onError: options.onError || null,
      onReady: options.onReady || null,
      assetBaseUrl: options.assetBaseUrl || '',
    };
    
    this.recognizer = null;
    this.onlineStream = null;
    this.punctuation = null;
    this.vadDetector = null;
    this.vadBuffer = null;
    this.stream = null;
    this.audioContext = null;
    this.processor = null;
    this.isRecording = false;
    this.isReady = false;
    this.Module = null;
    this.lastResult = '';
    this.lastPartial = '';
    this._lastPartialChangeMs = 0;
    this.wasmJs = this._resolveAssetUrl('lib/sherpa-onnx-wasm-main-asr.js');
    this.wasm = this._resolveAssetUrl('lib/sherpa-onnx-wasm-main-asr.wasm');
    this.asrHelperUrl = this._resolveAssetUrl('lib/sherpa-onnx-asr.js');
    
    // 支持通过 m_path 自动拼接 offlineWorker 路径
    let offlineWorkerUrl = this.options.modelPaths?.offlineWorker;
    if (!offlineWorkerUrl && this.options.modelPaths?.m_path) {
      const modelDir = this.options.modelPaths.m_path.replace(/\/$/, '');
      offlineWorkerUrl = `${modelDir}/offline-worker.js`;
    }
    this.offlineWorkerUrl = offlineWorkerUrl || this._resolveAssetUrl('lib/offline-worker.js');
    this.offlineRecognizer = null;
    this.offlineReady = null;
    this.offlinePendingJobs = new Map();
    this.offlineJobSeq = 0;
    this.segmentSamples = [];
    this.segmentSampleCount = 0;
    this.manualTwoPassSamples = [];
    this.manualTwoPassSampleCount = 0;
    this._twoPassWarmupStarted = false;
  }

  async init() {
    try {
      await this._loadWasmModule();
      await this._initOnlineRecognizer();
      console.log('[ASR] 在线识别器初始化成功01');
      // Always initialize punctuation engine (user controls via enabled flag)
      await this._initPunctuationEngine();
      console.log('[ASR] 在线识别器初始化成功02');
      // Initialize VAD if enabled
      if (this.options.vadMode === 'silero') {
        await this._initVadEngine();
      }
      console.log('[ASR] 在线识别器初始化成功03');
      this.isReady = true;
      
      if (this.options.onReady) {
        this.options.onReady();
      }
      console.log('[ASR] 在线识别器初始化成功04');
      // Optional pre-warm to reduce first two-pass latency
      this._warmupTwoPassWorker();
    } catch (error) {
      this._handleError('Initialization failed', error);
    }
  }

  async start() {
    if (!this.isReady) {
      throw new Error('ASR not initialized. Call init() first.');
    }
    
    if (this.isRecording) return;

    try {
      console.log('[ASR] Starting recording...');
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });
      console.log('[ASR] Got media stream');
      
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: this.options.sampleRate
      });
      console.log('[ASR] Created audio context, sampleRate:', this.audioContext.sampleRate);
      
      const source = this.audioContext.createMediaStreamSource(this.stream);
      console.log('[ASR] Created media stream source');
      
      this.processor = this.audioContext.createScriptProcessor(this.options.bufferSize, 1, 1);
      console.log('[ASR] Created script processor, bufferSize:', this.options.bufferSize);
      
      this.processor.onaudioprocess = (e) => {
        if (!this.isRecording) return;
        const samples = new Float32Array(e.inputBuffer.getChannelData(0));
        if (!this._audioEventCount) {
          this._audioEventCount = 0;
          console.log('[ASR] First onaudioprocess event received');
        }
        this._audioEventCount++;
        this._processAudio(samples);
      };
      
      source.connect(this.processor);
      console.log('[ASR] Connected source to processor');
      
      this.processor.connect(this.audioContext.destination);
      console.log('[ASR] Connected processor to destination');
      
      this.isRecording = true;
      console.log('[ASR] Recording started successfully');
      
    } catch (error) {
      this._handleError('Failed to start recording', error);
      throw error;
    }
  }

  stop() {
    if (!this.isRecording) return;

    // Flush VAD before we finalize the stream so pending segments are emitted
    this._flushVadDetector('stop');

    // Flush pending audio and emit the final result
    this._finalizeStream();
    this.isRecording = false;
    
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  async transcribeFile(file) {
    if (!file) {
      throw new Error('No audio file provided');
    }
    if (!this.isReady) {
      throw new Error('ASR not initialized. Call init() first.');
    }

    const arrayBuffer = await file.arrayBuffer();
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const decoded = await audioCtx.decodeAudioData(arrayBuffer);
    audioCtx.close?.().catch(() => {});

    const samples = decoded.getChannelData(0);
    const resampled = this._resampleToTargetRate(
        samples, decoded.sampleRate, this.options.sampleRate);
    if (!resampled || resampled.length === 0) {
      return '';
    }

    if (this.options.twoPass.enabled) {
      const offlineRecognizer = await this._ensureOfflineRecognizer();
      if (offlineRecognizer) {
        try {
          return this._runOfflineDecode(offlineRecognizer, resampled);
        } catch (err) {
          console.warn('[TwoPass] Offline decode failed, falling back to online recognizer', err);
          if (this.options.onTwoPassError) {
            this.options.onTwoPassError(err);
          }
        }
      } else {
        console.warn('[TwoPass] Offline assets not detected in bundle, using online recognizer only');
      }
    }

    return this._transcribeWithOnlineRecognizer(resampled);
  }

  getResult() {
    return this.lastResult;
  }

  clear() {
    this.lastResult = '';
    this.lastPartial = '';
    this._resetSegmentSamples();
    this._resetManualSamples();
    this._twoPassWarmupStarted = false;
  }

  destroy() {
    this.stop();
    if (this.onlineStream) {
      this.onlineStream.free?.();
      this.onlineStream = null;
    }
    if (this.recognizer) {
      this.recognizer.free?.();
      this.recognizer = null;
    }
    if (this.punctuation) {
      this.punctuation.free?.();
      this.punctuation = null;
    }
    if (this.vadDetector) {
      this.vadDetector.free?.();
      this.vadDetector = null;
    }
    if (this.vadBuffer) {
      this.vadBuffer.free?.();
      this.vadBuffer = null;
    }
    if (this.offlineRecognizer) {
      this.offlineRecognizer.free?.();
      this.offlineRecognizer = null;
    }
    if (this.offlineWorker) {
      try {
        this.offlineWorker.terminate();
      } catch (_) {}
      this.offlineWorker = null;
      this.offlineReady = null;
      this.offlinePendingJobs.clear();
    }
    this._resetSegmentSamples();
    this._resetManualSamples();
    this._lastPartialChangeMs = 0;
    this._twoPassWarmupStarted = false;
    this.isReady = false;
  }

  // Private methods

  _warmupTwoPassWorker() {
    if (!this.options.twoPass.enabled) return;
    if (!this.options.twoPass.autoWarmup) return;
    if (this._twoPassWarmupStarted) return;
    this._twoPassWarmupStarted = true;
    Promise.resolve(this._ensureOfflineWorker?.())
      .catch((err) => {
        console.warn('[TwoPass] warmup failed', err);
      });
  }
  
  async _loadWasmModule() {
    // 支持两种配置方式：
    // 1. m_path: 传递模型目录，SDK 自动拼接文件名
    // 2. data/wasmJs/wasm/asrJs: 传递具体文件路径（向后兼容）
    const modelDir = this.options.modelPaths?.m_path;
    let dataPath = this.options.modelPaths?.data;
    let jsPath = this.options.modelPaths?.wasmJs;
    let wasmPath = this.options.modelPaths?.wasm;
    let asrHelperPath = this.options.modelPaths?.asrJs;
    
    // 如果提供了 m_path，自动拼接文件路径
    if (modelDir) {
      const normalizedDir = modelDir.replace(/\/$/, ''); // 移除末尾斜杠
      dataPath = dataPath || `${normalizedDir}/sherpa-onnx-wasm-main-asr.data`;
      jsPath = jsPath || `${normalizedDir}/sherpa-onnx-wasm-main-asr.js`;
      wasmPath = wasmPath || `${normalizedDir}/sherpa-onnx-wasm-main-asr.wasm`;
      asrHelperPath = asrHelperPath || `${normalizedDir}/sherpa-onnx-asr.js`;
    }
    
    if (!dataPath) {
      throw new Error('Model data path not configured. Please provide modelPaths.m_path or modelPaths.data in options.');
    }
    
    // 默认基路径跟随包本身位置，避免硬编码 node_modules；也可通过 modelPaths/assetBaseUrl 自定义
    const defaultBase = this._resolveAssetUrl('lib/');
    const buildUrl = (name) => {
      try {
        return new URL(name, defaultBase).href;
      } catch (_) {
        const base = (defaultBase || '').replace(/\/$/, '');
        const clean = (name || '').replace(/^\//, '');
        return `${base}/${clean}`;
      }
    };
    
    // 如果没有提供路径，使用默认路径（向后兼容）
    jsPath = jsPath || buildUrl('sherpa-onnx-wasm-main-asr.js');
    wasmPath = wasmPath || buildUrl('sherpa-onnx-wasm-main-asr.wasm');
    asrHelperPath = asrHelperPath || buildUrl('sherpa-onnx-asr.js');
    this._asrHelperPath = asrHelperPath;
    
    console.log('Loading WASM from:', { jsPath, wasmPath, dataPath });
    
    // 配置 Module 的文件路径（需在加载 wasm 之前设置）
    // 始终使用一个新的 Module 实例，避免旧的全局残留状态
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('WASM module load timeout (30s)'));
      }, 30000);

      window.Module = {
        locateFile: (path) => {
          if (path.endsWith('.wasm')) return wasmPath;
          if (path.endsWith('.data')) return dataPath;
          return path;
        },
        print: (...args) => console.log('[WASM]', ...args),
        printErr: (...args) => console.error('[WASM]', ...args),
        onAbort: (reason) => {
          console.error('[WASM] Abort:', reason);
          clearTimeout(timeoutId);
          reject(new Error(`WASM aborted: ${reason}`));
        },
        onRuntimeInitialized: () => {
          // Wait a tick to ensure all exports are available
          setTimeout(() => {
            const Module = window.Module;
            const ready = typeof Module._malloc === 'function' &&
                          typeof Module._SherpaOnnxCreateOnlineRecognizer === 'function' &&
                          typeof window.createOnlineRecognizer === 'function';
            
            if (ready) {
              clearTimeout(timeoutId);
              this.Module = Module;
              console.log('WASM runtime initialized successfully');
              resolve();
            } else {
              clearTimeout(timeoutId);
              reject(new Error('WASM module loaded but required functions not found'));
            }
          }, 0);
        }
      };

      // Load scripts (include VAD if needed)
      const scriptsToLoad = [
        this._loadScript(jsPath),
        this._loadScript(asrHelperPath)
      ];
      
      if (this.options.vadMode === 'silero') {
        let vadHelperPath = this.options.modelPaths?.vadJs;
        if (!vadHelperPath && modelDir) {
          vadHelperPath = `${modelDir.replace(/\/$/, '')}/sherpa-onnx-vad.js`;
        }
        vadHelperPath = vadHelperPath || buildUrl('sherpa-onnx-vad.js');
        scriptsToLoad.push(this._loadScript(vadHelperPath));
      }
      
      Promise.all(scriptsToLoad).catch(err => {
        clearTimeout(timeoutId);
        reject(err);
      });
    });
  }

  async _loadScript(src, options = {}) {
    const {forceReload = false} = options;
    // Avoid duplicate identifier errors; if already present assume loaded
    const target = new URL(src, window.location.href).href;
    const existing = Array.from(document.scripts || []).find((s) => s.src === target);
    if (existing && !forceReload) {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = forceReload ? `${src}?reload=${Date.now()}` : src;
      script.onload = () => {
        console.log('[Script] 已加载:', script.src);
        resolve();
      };
      script.onerror = () => reject(new Error(`Failed to load script: ${script.src}`));
      document.head.appendChild(script);
    });
  }

  async _initOnlineRecognizer() {
    if (!this.Module) {
      throw new Error('WASM Module not loaded');
    }
    
    if (typeof createOnlineRecognizer !== 'function') {
      throw new Error('sherpa-onnx helper not loaded');
    }

    const required = [
      './encoder.onnx',
      './decoder.onnx',
      './joiner.onnx',
      './tokens.txt',
    ];
    if (typeof this.Module._SherpaOnnxFileExists === 'function') {
      const missing = required.filter((file) => !this._fileExists(file));
      if (missing.length > 0) {
        throw new Error(`Missing online model assets in .data: ${missing.join(', ')}`);
      }
    } else {
      console.warn('[OnlineRecognizer] _SherpaOnnxFileExists not exposed, skip asset pre-check');
    }

    // Use default config packaged inside sherpa-onnx data archive
    try {
      this.recognizer = createOnlineRecognizer(this.Module);
      this.onlineStream = this.recognizer.createStream();
      console.log('Online recognizer initialized');
    } catch (err) {
      // Extra diagnostics to surface file availability
      try {
        const exists = required.map((f) => `${f}=${this._fileExists(f)}`);
        console.error('[OnlineRecognizer] asset check:', exists.join(', '));
        if (this.Module && this.Module.FS && this.Module.FS.readdir) {
          const entries = this.Module.FS.readdir('/').slice(0, 40);
          console.error('[OnlineRecognizer] FS root entries:', entries);
        }
      } catch (logErr) {
        console.error('[OnlineRecognizer] extra diagnostics failed', logErr);
      }
      throw new Error(`[OnlineRecognizer] Failed to create instance: ${err?.message || err}`);
    }
  }

  async _initPunctuationEngine() {
    console.log('[Punctuation] 开始初始化标点引擎...');
    
    if (!this.Module) {
      console.error('[Punctuation] 错误：WASM Module 未加载');
      return;  // Don't throw error, just skip initialization
    }

    const PunctCtor = this._getGlobalHelper('OfflinePunctuation');
    if (typeof PunctCtor !== 'function') {
      console.warn('[Punctuation] OfflinePunctuation 类不可用，跳过标点引擎初始化');
      return;
    }

    try {
      const config = {
        model: {
          ctTransformer: './punct-ct-transformer.onnx',  // Model path in .data file
          numThreads: 1,
          debug: 0,  // Turn off debug to reduce log spam
          provider: 'cpu',
        }
      };
      
      this.punctuation = new PunctCtor(config, this.Module);
      
      if (this.punctuation.handle === 0) {
        console.warn('[Punctuation] 标点引擎创建失败（handle=0），可能模型不存在，继续使用无标点模式');
        this.punctuation = null;
      } else {
        console.log('[Punctuation] 标点引擎初始化成功');
      }
    } catch (error) {
      console.warn('[Punctuation] 标点引擎初始化失败，继续使用无标点模式:', error.message);
      this.punctuation = null;
    }
  }

  async _initVadEngine() {
    console.log('[VAD] 开始初始化 Silero VAD...');
    
    if (!this.Module) {
      console.error('[VAD] 错误：WASM Module 未加载');
      throw new Error('WASM Module not loaded');
    }

    if (typeof window.createVad !== 'function') {
      console.warn('[VAD] createVad 函数不可用');
      return;
    }

    try {
      const vadConfig = {
        sileroVad: {
          model: './silero_vad.onnx',
          threshold: this.options.vad.silero.threshold,
          minSilenceDuration: this.options.vad.silero.minSilenceDuration,
          minSpeechDuration: this.options.vad.silero.minSpeechDuration,
          maxSpeechDuration: this.options.vad.silero.maxSpeechDuration,
          windowSize: this.options.vad.silero.windowSize,
        },
        tenVad: {
          model: '',  // Not using TEN VAD
        },
        sampleRate: this.options.sampleRate,
        numThreads: 1,
        provider: 'cpu',
        debug: 0,
        bufferSizeInSeconds: this.options.vad.bufferSizeInSeconds,
      };

      console.log('[VAD] 创建配置:', vadConfig);
      this.vadDetector = window.createVad(this.Module, vadConfig);
      
      const bufferSamples = vadConfig.bufferSizeInSeconds * this.options.sampleRate;
      if (typeof window.CircularBuffer === 'function') {
        this.vadBuffer = new window.CircularBuffer(bufferSamples, this.Module);
        console.log('[VAD] CircularBuffer 已创建，容量:', bufferSamples);
      } else {
        this.vadBuffer = this._createJsCircularBuffer(bufferSamples);
        console.log('[VAD] 使用 JS 循环缓冲，容量:', bufferSamples);
      }
      
      console.log('[VAD] Silero VAD 初始化成功');
    } catch (error) {
      console.error('[VAD] VAD 引擎初始化失败:', error);
      this.vadDetector = null;
      this.vadBuffer = null;
    }
  }

  _processAudio(samples) {
    if (!this.recognizer || !this.onlineStream) {
      console.log('[ASR] _processAudio: recognizer or stream not ready');
      return;
    }

    const sourceRate = this.audioContext?.sampleRate || this.options.sampleRate;
    const resampled = this._resampleToTargetRate(samples, sourceRate, this.options.sampleRate);
    if (!resampled.length) {
      console.log('[ASR] _processAudio: no resampled data');
      return;
    }
    this._appendSegmentSamples(resampled);
    
    const skipOnline = this._isManualTwoPassMode() && this.options.twoPass.disableOnlineInManual === true;
    if (skipOnline) {
      // Manual mode with online disabled: only collect audio for two-pass
      return;
    }
    
    // Log first call
    if (!this._audioProcessCount) {
      this._audioProcessCount = 0;
    }
    this._audioProcessCount++;
    if (this._audioProcessCount % 50 === 1) {
      console.log('[ASR] _processAudio called, count:', this._audioProcessCount, 'samples:', resampled.length);
    }

    // ALWAYS process through ASR (exactly like app-asr.js)
    this.onlineStream.acceptWaveform(this.options.sampleRate, resampled);
    
    // Decode loop
    while (this.recognizer.isReady(this.onlineStream)) {
      this.recognizer.decode(this.onlineStream);
    }

     console.log('[VAD] Feeding samples to VAD:', this.vadDetector, this.vadBuffer, this.options.vadMode);
    // If VAD is enabled, feed samples to VAD (for segmentation, parallel to ASR)
      if (this.vadDetector && this.vadBuffer) {
        this._feedVad(resampled);
      } else if (this.options.vadMode === 'silero') {
        if (!this._loggedMissingVad) {
          console.log('[VAD] Skipping feed - detector/buffer not ready');
          this._loggedMissingVad = true;
        }
      }
    
    // Check if we've reached an endpoint
    const isEndpoint = this.recognizer.isEndpoint(this.onlineStream);
    const vadEnabled = !!(this.vadDetector && this.vadBuffer);
    let shouldResetStream = false;

    const emitWithAudio = (text, reason) => {
      const audioSamples = this._takeSegmentSamples();
      this._emitFinalResult(text, {audioSamples, reason});
      this.recognizer.reset(this.onlineStream);
    };
    
    if (!vadEnabled) {
      // When VAD is disabled, reset at endpoint
      shouldResetStream = isEndpoint;
    }
    
    // Get result after decode loop (not inside it!)
    let result = this.recognizer.getResult(this.onlineStream).text;
    
    // For Paraformer models, add tail padding and decode again
    if (this.recognizer.config && 
        this.recognizer.config.modelConfig && 
        this.recognizer.config.modelConfig.paraformer && 
        this.recognizer.config.modelConfig.paraformer.encoder != '') {
      const tailPaddings = new Float32Array(this.options.sampleRate);
      this.onlineStream.acceptWaveform(this.options.sampleRate, tailPaddings);
      while (this.recognizer.isReady(this.onlineStream)) {
        this.recognizer.decode(this.onlineStream);
      }
      result = this.recognizer.getResult(this.onlineStream).text;
    }
    
    // Emit partial result immediately if there's text
    if (result && result.length > 0) {
      if (result !== this.lastPartial) {
        this.lastPartial = result;
        this._lastPartialChangeMs = Date.now();
        console.log('[ASR] Partial result:', result.substring(0, 50));
        if (this.options.onPartial) {
          this.options.onPartial(result);
        }
      }
    }
    
    // If VAD is enabled but doesn't emit and partial停滞，则按超时切段
    if (vadEnabled && result && result.length > 0 && this._lastPartialChangeMs) {
      const idleMs = Date.now() - this._lastPartialChangeMs;
      const fallbackIdleMs = 1500; // 1.5s 无变化则视为一句结束
      if (idleMs > fallbackIdleMs) {
        console.log('[ASR] Partial idle for', idleMs, 'ms - fallback emit (VAD idle)');
        emitWithAudio(result, 'vad-fallback');
        return;
      }
    }
    
    // Fallback: when VAD is enabled but fails to emit, still cut by ASR endpoint
    if (vadEnabled && isEndpoint && result && result.length > 0) {
      console.log('[ASR] Endpoint reached (VAD enabled) - fallback emit');
      emitWithAudio(result, 'vad-endpoint');
      return;
    }
    
    // Reset stream at endpoint (only when VAD is disabled)
    if (shouldResetStream) {
      console.log('[ASR] Endpoint reached (VAD disabled), emitting final result');
      emitWithAudio(result, 'asr-endpoint');
    }
  }

  _feedVad(samples) {
    if (!this.vadDetector || !this.vadBuffer || !samples || samples.length === 0) {
      return;
    }

    if (!this._vadFeedLogged) {
      console.log('[VAD] Feeding VAD with samples, length:', samples.length);
      this._vadFeedLogged = true;
    }

    // Feed samples to VAD buffer
    this.vadBuffer.push(samples);
    
    const windowSize = this.options.vad.silero.windowSize;
    
    if (!this._vadFeedCount) {
      this._vadFeedCount = 0;
    }
    this._vadFeedCount++;
    
    // Process VAD in chunks
    while (this.vadBuffer.size() > windowSize) {
      const chunk = this.vadBuffer.get(this.vadBuffer.head(), windowSize);
      this.vadDetector.acceptWaveform(chunk);
      this.vadBuffer.pop(windowSize);
      
      // Log periodically to confirm VAD is running (every ~2.5s with default buffer)
      if (this._vadFeedCount % 10 === 1) {
        console.log('[VAD] Processing, buffer size:', this.vadBuffer.size(), 'isEmpty:', this.vadDetector.isEmpty());
      }

      this._drainVadSegments('process');
    }
  }

  _drainVadSegments(reason = 'process') {
    if (!this.vadDetector) return;
    while (!this.vadDetector.isEmpty()) {
      const segment = this.vadDetector.front();
      this.vadDetector.pop();
      
      console.log('[VAD] Speech segment detected!', {
        samples: segment?.samples?.length || 0,
        start: segment?.start || 0,
        reason,
      });
      const audioSamples = this._takeSegmentSamples();
      
      // VAD detected end of speech segment - emit final result and reset
      if (segment && segment.samples && segment.samples.length > 0) {
        const currentResult = this.lastPartial || this.recognizer.getResult(this.onlineStream).text;
        console.log('[VAD] Current result for segment:', currentResult?.substring(0, 50) || '(empty)');
        if (currentResult && currentResult.length > 0) {
          console.log('[VAD] Emitting final result:', currentResult);
          this._emitFinalResult(currentResult, {audioSamples, reason: `vad-${reason}`});
          this.recognizer.reset(this.onlineStream);
        } else {
          console.log('[VAD] Skipping segment - no text result');
          this._resetSegmentSamples();
        }
      }
    }
  }

  _flushVadDetector(reason = 'flush') {
    if (!this.vadDetector) return;
    try {
      this.vadDetector.flush();
      this._drainVadSegments(reason);
    } catch (err) {
      console.warn('[VAD] Flush failed:', err?.message || err);
    }
  }

  _handleError(message, error) {
    console.error(message, error);
    
    if (this.options.onError) {
      this.options.onError({ message, error });
    }
  }

  _decodeLoop(forceEndpoint = false, audioSamples = null, reason = 'force') {
    if (!this.recognizer || !this.onlineStream) return;

    // Simple decode loop - just decode while ready
    while (this.recognizer.isReady(this.onlineStream)) {
      this.recognizer.decode(this.onlineStream);
    }

    // Get final result when forced
    if (forceEndpoint) {
      const result = this.recognizer.getResult(this.onlineStream);
      const text = result?.text || '';
      const samples = audioSamples || this._takeSegmentSamples();
      this._emitFinalResult(text, {audioSamples: samples, reason});
      this.recognizer.reset(this.onlineStream);
    }
  }

  _finalizeStream() {
    if (!this.recognizer || !this.onlineStream) return;
    this.onlineStream.inputFinished?.();
    this._decodeLoop(true, this._takeSegmentSamples(), 'finalize');
    this.lastPartial = '';
  }

  _emitFinalResult(text, options = {}) {
    const {audioSamples = null, reason = ''} = options;
    const originalText = text || this.lastPartial;
    
    console.log('[ASR] _emitFinalResult called with:', originalText);
    if (originalText) {
      this.lastResult = originalText;
      if (this.options.onResult) {
        console.log('[ASR] Calling onResult callback with:', originalText.substring(0, 50));
        this.options.onResult(originalText);
      } else {
        console.warn('[ASR] onResult callback not defined!');
      }
    }
    this.lastPartial = '';
    this._lastPartialChangeMs = 0;
    const hasAudioSamples = !!(audioSamples && audioSamples.length);
    const manualHasAudio = this._isManualTwoPassMode() && this.manualTwoPassSampleCount > 0;
    const shouldRunTwoPass = this.options.twoPass.enabled &&
      (hasAudioSamples || manualHasAudio) &&
      this._shouldRunManualTwoPass(reason);
    if (shouldRunTwoPass) {
      const samplesForTwoPass = this._isManualTwoPassMode()
        ? (this._takeManualSamples() || audioSamples)
        : audioSamples;
      this._runTwoPass(samplesForTwoPass, originalText);
    } else if (!audioSamples || audioSamples.length === 0) {
      // Ensure we don't keep stale samples when no audio was passed in
      this._resetSegmentSamples();
    }
  }

  _appendSegmentSamples(chunk) {
    if (!this.options.twoPass.enabled) {
      return;
    }
    if (!chunk || chunk.length === 0) {
      return;
    }
    const copy = new Float32Array(chunk);
    this.segmentSamples.push(copy);
    this.segmentSampleCount += chunk.length;
    if (this._isManualTwoPassMode()) {
      this.manualTwoPassSamples.push(copy);
      this.manualTwoPassSampleCount += chunk.length;
    }
  }

  _takeSegmentSamples() {
    if (!this.segmentSampleCount) {
      this._resetSegmentSamples();
      return null;
    }
    const merged = new Float32Array(this.segmentSampleCount);
    let offset = 0;
    for (const chunk of this.segmentSamples) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    this._resetSegmentSamples();
    return merged;
  }

  _resetSegmentSamples() {
    this.segmentSamples = [];
    this.segmentSampleCount = 0;
  }

  _takeManualSamples() {
    if (!this.manualTwoPassSampleCount) {
      this._resetManualSamples();
      return null;
    }
    const merged = new Float32Array(this.manualTwoPassSampleCount);
    let offset = 0;
    for (const chunk of this.manualTwoPassSamples) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    this._resetManualSamples();
    return merged;
  }

  _resetManualSamples() {
    this.manualTwoPassSamples = [];
    this.manualTwoPassSampleCount = 0;
  }

  _isManualTwoPassMode() {
    return this.options.twoPass.enabled && this.options.twoPass.mode === 'manual-stop';
  }

  _shouldRunManualTwoPass(reason = '') {
    if (!this._isManualTwoPassMode()) return true;
    const normalized = reason.toLowerCase();
    return normalized === 'finalize' || normalized === 'stop' || normalized === 'manual-stop';
  }

  async _runTwoPass(audioSamples, streamingText = '') {
    if (!audioSamples || audioSamples.length === 0) {
      this._resetSegmentSamples();
      return;
    }

    try {
      await this._dispatchTwoPassJob(audioSamples, streamingText);
    } catch (err) {
      console.warn('[TwoPass] Offline decode failed', err);
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
    } finally {
      this._resetSegmentSamples();
    }
  }

  _getGlobalHelper(name) {
    // Try direct binding (may be lexical, not on window)
    try {
      // eslint-disable-next-line no-eval
      const value = eval(name);
      if (typeof value !== 'undefined') return value;
    } catch (_) {
      // ignored
    }
    if (typeof globalThis !== 'undefined' && name in globalThis) {
      return globalThis[name];
    }
    if (typeof window !== 'undefined' && name in window) {
      return window[name];
    }
    return null;
  }

  _resolveAssetUrl(relativePath) {
    const normalized = (relativePath || '').replace(/^\.?\//, '');
    if (this.options.assetBaseUrl) {
      return `${this.options.assetBaseUrl.replace(/\/$/, '')}/${normalized}`;
    }

    // Try to resolve assets relative to the package root so dev builds that
    // import from `src/` still find `lib/*` resources.
    const candidates = [];
    try {
      if (typeof import.meta !== 'undefined' && import.meta.url) {
        const metaUrl = import.meta.url;
        const pathname = new URL(metaUrl).pathname || '';
        const inSrc = pathname.includes('/src/');

        // When running against source files there is no src/lib, so jump to
        // the package root. For built artifacts keep the sibling lib fallback.
        if (inSrc) {
          candidates.push(new URL(`../${normalized}`, metaUrl).href);
          candidates.push(new URL(normalized, metaUrl).href);
        } else {
          candidates.push(new URL(normalized, metaUrl).href);
          candidates.push(new URL(`../${normalized}`, metaUrl).href);
        }
      }
    } catch (_) {
      // Ignore and fall through to other strategies
    }
    if (typeof document !== 'undefined' && document.currentScript?.src) {
      try {
        candidates.push(new URL(`../${normalized}`, document.currentScript.src).href);
        candidates.push(new URL(normalized, document.currentScript.src).href);
      } catch (_) {
        // Ignore and fall through
      }
    }
    if (candidates.length > 0) {
      return candidates[0];
    }
    return `speech-asr/${normalized}`;
  }

  async _ensureOfflineRecognizer() {
    if (!this.options.twoPass.enabled) {
      return null;
    }
    if (this.offlineRecognizer) {
      return this.offlineRecognizer;
    }
    if (!this.Module) {
      console.warn('[TwoPass] Module not ready, skip offline recognizer');
      return null;
    }
    const OfflineCtor = this._getGlobalHelper('OfflineRecognizer');
    if (typeof OfflineCtor !== 'function') {
      console.warn('[TwoPass] OfflineRecognizer helper is missing (helper script may not have executed)');
      return null;
    }

    const probePaths = (name) => {
      const candidates = [
        `./${name}`,
        name,
        `/${name}`,
      ];
      for (const path of candidates) {
        if (this._fileExists(path)) return true;
      }
      return false;
    };

    const modelExists = probePaths('second-pass-sense-voice.onnx');
    const tokensExists = probePaths('second-pass-tokens.txt');
    console.log('[TwoPass] asset probe', {
      'second-pass-sense-voice.onnx': modelExists,
      'second-pass-tokens.txt': tokensExists,
    });
    try {
      if (this.Module && this.Module.FS && typeof this.Module.FS.readdir === 'function') {
        console.log('[TwoPass] FS root entries:', this.Module.FS.readdir('/'));
      }
    } catch (err) {
      console.warn('[TwoPass] Failed to list FS root', err);
    }
    const normalize = (p) => {
      if (!p) return p;
      if (p.startsWith('./')) return p;
      if (p.startsWith('/')) return `.${p}`;
      return `./${p}`;
    };

    let resolvedModel = modelExists ? './second-pass-sense-voice.onnx' :
        this._findFileBySubstring('second-pass-sense-voice');
    let resolvedTokens = tokensExists ? './second-pass-tokens.txt' :
        this._findFileBySubstring('second-pass-tokens');

    resolvedModel = normalize(resolvedModel);
    resolvedTokens = normalize(resolvedTokens);

    if (!resolvedModel || !resolvedTokens) {
      console.warn('[TwoPass] Offline assets not detected in .data bundle');
      return null;
    }

    const config = this._buildOfflineRecognizerConfig();
    config.modelConfig.senseVoice.model = resolvedModel;
    config.modelConfig.tokens = resolvedTokens;
    console.log('[TwoPass] Using offline assets:', {model: resolvedModel, tokens: resolvedTokens});
    try {
      this.offlineRecognizer = new OfflineCtor(config, this.Module);
      console.log('[TwoPass] Offline recognizer initialised');
      return this.offlineRecognizer;
    } catch (err) {
      console.error('[TwoPass] Failed to init OfflineRecognizer', err);
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
      return null;
    }
  }

  _buildOfflineRecognizerConfig() {
    return {
      modelConfig: {
        tokens: './second-pass-tokens.txt',
        numThreads: 1,
        debug: 0,
        senseVoice: {
          model: './second-pass-sense-voice.onnx',
          language: 'auto',
          useInverseTextNormalization: 1,
        },
      },
      featConfig: {
        sampleRate: this.options.sampleRate,
        featureDim: 80,
      },
      decodingMethod: 'greedy_search',
      maxActivePaths: 4,
    };
  }

  _buildOfflineRuntimePaths() {
    const toAbs = (url) => {
      if (!url) return '';
      try {
        const base = (typeof window !== 'undefined' && window.location?.href) ?
            window.location.href : undefined;
        return base ? new URL(url, base).href : new URL(url).href;
      } catch (_) {
        return url;
      }
    };
    const baseUrl = (() => {
      try {
        return new URL('./', this.offlineWorkerUrl).href;
      } catch (_) {
        return '';
      }
    })();
    return {
      baseUrl,
      wasmJs: toAbs(this.wasmJs),
      asrJs: toAbs(this.asrHelperUrl),
      wasm: toAbs(this.wasm),
      data: toAbs(this.options.modelPaths?.data || ''),
    };
  }

  _fileExists(filename) {
    if (!this.Module || typeof this.Module._SherpaOnnxFileExists !== 'function') {
      return false;
    }
    const filenameLen = this.Module.lengthBytesUTF8(filename) + 1;
    const buffer = this.Module._malloc(filenameLen);
    this.Module.stringToUTF8(filename, buffer, filenameLen);
    const exists = this.Module._SherpaOnnxFileExists(buffer) === 1;
    this.Module._free(buffer);
    return exists;
  }

  _findFileBySubstring(substring) {
    if (!this.Module || !this.Module.FS || typeof this.Module.FS.readdir !== 'function') {
      return '';
    }
    const queue = ['/'];
    const visited = new Set();
    while (queue.length) {
      const dir = queue.shift();
      if (visited.has(dir)) continue;
      visited.add(dir);
      let entries = [];
      try {
        entries = this.Module.FS.readdir(dir);
      } catch (err) {
        continue;
      }
      for (const entry of entries) {
        if (entry === '.' || entry === '..') continue;
        const fullPath = dir.endsWith('/') ? `${dir}${entry}` : `${dir}/${entry}`;
        if (entry.includes(substring)) {
          return fullPath;
        }
        // crude heuristic: assume files have extension
        if (!entry.includes('.')) {
          queue.push(fullPath);
        }
      }
    }
    return '';
  }

  async _ensureOfflineWorker() {
    if (!this.options.twoPass.enabled) {
      return null;
    }
    if (this.offlineWorker && this.offlineReady) {
      return this.offlineReady;
    }

    const workerScript = this.offlineWorkerUrl;
    try {
      this.offlineWorker = new Worker(workerScript);
    } catch (err) {
      console.error('[TwoPass] Failed to create offline worker', err);
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
      return null;
    }

    this.offlineReady = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Offline worker init timeout'));
      }, 10000);

      const handleMessage = (event) => {
        const data = event.data || {};
        if (data.type === 'ready') {
          clearTimeout(timeout);
          resolve(true);
          return;
        }
        if (data.type === 'init-error') {
          clearTimeout(timeout);
          reject(new Error(data.message || 'Offline worker init error'));
          return;
        }
        if (data.type === 'decode-result' || data.type === 'decode-error') {
          this._handleOfflineWorkerResult(data);
        }
      };

      this.offlineWorker.addEventListener('message', handleMessage);
      this.offlineWorker.addEventListener('error', (event) => {
        clearTimeout(timeout);
        reject(new Error(event.message || 'Offline worker error'));
      });
    });

    const config = this._buildOfflineRecognizerConfig();
    const runtimePaths = this._buildOfflineRuntimePaths();
    this.offlineWorker.postMessage({
      type: 'init',
      config,
      sampleRate: this.options.sampleRate,
      runtimePaths,
    });

    return this.offlineReady.catch((err) => {
      console.error('[TwoPass] Offline worker init failed', err);
      this.offlineWorker = null;
      this.offlineReady = null;
      return null;
    });
  }

  async _dispatchTwoPassJob(audioSamples, streamingText) {
    const ready = await this._ensureOfflineWorker();
    if (!ready) {
      const err = new Error('Offline worker not ready');
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
      return;
    }

    const jobId = this.offlineJobSeq++;
    this.offlinePendingJobs.set(jobId, {streamingText});

    try {
      this.offlineWorker.postMessage({
        type: 'decode',
        id: jobId,
        sampleRate: this.options.sampleRate,
        audioBuffer: audioSamples.buffer,
      }, [audioSamples.buffer]);
    } catch (err) {
      console.error('[TwoPass] Failed to post decode job', err);
      this.offlinePendingJobs.delete(jobId);
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
    }
  }

  _handleOfflineWorkerResult(data) {
    const jobId = data.id;
    const pending = this.offlinePendingJobs.get(jobId);
    if (!pending) return;
    this.offlinePendingJobs.delete(jobId);

    if (data.type === 'decode-result') {
      const offlineText = (data.result?.text || '').trim();
      if (offlineText) {
        if (this.options.onTwoPassResult) {
          this.options.onTwoPassResult({offlineText, streamingText: pending.streamingText});
        }
      }
    } else {
      const err = new Error(data.message || 'Offline decode error');
      if (this.options.onTwoPassError) {
        this.options.onTwoPassError(err);
      }
    }
  }

  _transcribeWithOnlineRecognizer(samples) {
    if (!this.recognizer) {
      throw new Error('Online recognizer not initialized');
    }
    const stream = this.recognizer.createStream();
    stream.acceptWaveform(this.options.sampleRate, samples);
    while (this.recognizer.isReady(stream)) {
      this.recognizer.decode(stream);
    }
    const paraformer = this.recognizer.config?.modelConfig?.paraformer || {};
    if (paraformer && paraformer.encoder) {
      const tail = new Float32Array(this.options.sampleRate);
      stream.acceptWaveform(this.options.sampleRate, tail);
      while (this.recognizer.isReady(stream)) {
        this.recognizer.decode(stream);
      }
    }
    stream.inputFinished?.();
    while (this.recognizer.isReady(stream)) {
      this.recognizer.decode(stream);
    }
    const result = this.recognizer.getResult(stream);
    stream.free?.();
    return result?.text || '';
  }

  _createJsCircularBuffer(capacity) {
    const data = new Float32Array(capacity);
    let start = 0;
    let length = 0;
    return {
      push(samples) {
        for (let i = 0; i < samples.length; i++) {
          const idx = (start + length) % capacity;
          data[idx] = samples[i];
          if (length < capacity) {
            length++;
          } else {
            start = (start + 1) % capacity;
          }
        }
      },
      get(headIndex, windowSize) {
        const out = new Float32Array(windowSize);
        for (let i = 0; i < windowSize; i++) {
          const idx = (start + i) % capacity;
          out[i] = data[idx];
        }
        return out;
      },
      pop(windowSize) {
        const remove = Math.min(windowSize, length);
        start = (start + remove) % capacity;
        length -= remove;
      },
      size() {
        return length;
      },
      head() {
        return start;
      },
    };
  }

  _resampleToTargetRate(samples, sourceRate, targetRate) {
    if (!samples || samples.length === 0) return new Float32Array(0);
    if (!sourceRate || sourceRate === targetRate) return new Float32Array(samples);
    if (!targetRate) return new Float32Array(samples);

    const durationSeconds = samples.length / sourceRate;
    const targetLength = Math.max(1, Math.round(durationSeconds * targetRate));
    const result = new Float32Array(targetLength);
    const ratio = sourceRate / targetRate;
    let position = 0;
    for (let i = 0; i < targetLength; ++i) {
      const index = Math.floor(position);
      const frac = position - index;
      const nextIndex = Math.min(samples.length - 1, index + 1);
      const current = samples[index] || 0;
      const next = samples[nextIndex] || 0;
      result[i] = current + (next - current) * frac;
      position += ratio;
    }
    return result;
  }
}

export default SpeechASR;
